import { useSyncExternalStore } from 'react';
import API from '../../api/api';
import { constructMediaUrl } from '../../api/helpers';
import { readContentVersion } from '../../api/mediaUrls';
import { ClipPrompt } from '../../api/structs/clip';
import { MediaItem } from '../../api/structs/media';
import { ReelReviewEnvelope } from '../../api/structs/reelReview';
import {
  computeTimeline,
  effectiveDurationSeconds,
  hasInvertedTrim,
  isTransitionName,
  Timeline,
} from '../../remotion/shared/metadata/timeline';
import { booleanValue, stringValue } from '../../remotion/shared/metadata/values';
import { ReelSlide, TransitionName } from '../../remotion/shared/types';
import { ClipRenderFields } from '../clips/renderState';
import {
  MEDIA_LIBRARY_INVALIDATE_EVENT,
  MediaLibraryInvalidateDetail,
} from '../../hooks/mediaLibraryEvents';
import { slideSubtitleEnabled } from './globalToggles';
import {
  CaptionSegment,
  NATIVE_AUDIO_KEYS,
  readNativeVolumePoints,
  serializeCaptionSegments,
  serializeVolumePoints,
  TRACK_KEYS,
  TrackLane,
} from './narrationModel';
import {
  buildReelReviewBoundaries,
  buildReelReviewGaps,
  computeBoundariesHash,
  IgnoredSuggestion,
  planStagedChanges,
  StagedChange,
} from './reelReviewStaging';
import { getSourceDuration } from './sourceDurations';
import { VolumePoint } from '../../remotion/shared/metadata/envelope';

/**
 * Single-editor store for the clip video editor, in the house pattern
 * (module-level state + useSyncExternalStore, see clips/renderState.ts).
 *
 * It holds drafts — clip metadata and a full metadata map per media item — and
 * derives the renderer's slide list from them, so the `<Player>` preview is
 * driven by exactly the values a render would use. Edits are flushed to the
 * backend on a debounce; `waitForPendingSaves()` gates Render the same way the
 * storyboard editor's does (clips/useClipDraftEditor.ts).
 */

export const EDITOR_FPS = 30;
export const FLUSH_DEBOUNCE_MS = 800;
/** No block may be dragged shorter than this — a slide of a few frames is unusable. */
export const MIN_SLIDE_DURATION_SECONDS = 0.5;

/** The slide-bearing half of a clip's media (audio is not a slide). */
export interface EditorMedia {
  id: string;
  kind: 'image' | 'video';
  fileUrl: string;
  /**
   * `metadata.content_version` of the bytes behind `fileUrl`, when the item
   * carries one. Regeneration rewrites an item in place — same id, same
   * `file_url`, same proxy path — so this is the only thing that distinguishes
   * the new bytes from the ones the browser (and Remotion's prefetch blob)
   * already cached. Absent → URLs stay exactly as they were.
   */
  contentVersion?: number;
}

export interface ClipEditorState {
  clipId: string;
  status: 'idle' | 'loading' | 'ready' | 'error';
  /** Why the clip could not be loaded (only meaningful for status 'error'). */
  error: string;
  /** Last failed flush, kept separate from `error`: the editor stays usable. */
  saveError: string;
  clipName: string;
  clipStyle: string;
  fileUrls: string[];
  /** Served render status, seeded into the shared render-state store. */
  renderFields: ClipRenderFields;
  /** Draft clip metadata (server values plus unflushed edits). */
  clipMetadata: Record<string, any>;
  /** mediaId → full draft metadata map (the replace-metadata contract). */
  mediaDrafts: Record<string, Record<string, any>>;
  media: EditorMedia[];
  /**
   * Audio the clip can resolve a music/narrator track from: its own audio
   * bucket plus any library item bound from the editor (a library pick is not
   * in the clip's bucket until the backend attaches it, but the render path
   * resolves it from the store, so the editor caches it here to match).
   */
  audios: MediaItem[];
  /**
   * The clip exactly as the backend served it. The scene side panel drives the
   * existing storyboard components, which speak `MediaItem`, not drafts.
   */
  clip: ClipPrompt | null;
  /** Derived from `media` + `mediaDrafts`, ordered as the renderer orders them. */
  slides: ReelSlide[];
  timeline: Timeline;
  selectedSlideId: string | null;
  /**
   * The caption segment the properties panel is editing. One selection, two
   * shapes: a slide and a segment are never selected at once, because the panel
   * can only show one of them.
   */
  selectedSegmentId: string | null;
  pendingSaves: number;
  dirty: boolean;
  reelReview: ReelReviewState;
  /** The suggestions currently applied-but-not-yet-accepted (the staged preview). */
  stagedChanges: StagedChange[];
  /** Suggestions from the same report that failed client-side validation — never staged. */
  ignoredSuggestions: IgnoredSuggestion[];
}

/**
 * `envelope` mirrors `clipMetadata.reel_review` (server truth — reflected on
 * every load/refresh, exactly like a scene's `review_verdict`). `requesting`/
 * `error` are local request-lifecycle flags with no server counterpart, and
 * `stale` is set only at the moment a report is staged, by comparing its
 * `boundaries_hash` against the CURRENT timeline's.
 */
export interface ReelReviewState {
  requesting: boolean;
  error: string;
  envelope: ReelReviewEnvelope | null;
  stale: boolean;
}

const EMPTY_REEL_REVIEW: ReelReviewState = { requesting: false, error: '', envelope: null, stale: false };

const EMPTY_STATE: ClipEditorState = {
  clipId: '',
  status: 'idle',
  error: '',
  saveError: '',
  clipName: '',
  clipStyle: '',
  fileUrls: [],
  renderFields: {},
  clipMetadata: {},
  mediaDrafts: {},
  media: [],
  audios: [],
  clip: null,
  slides: [],
  timeline: { entries: [], totalDurationInFrames: 1 },
  selectedSlideId: null,
  selectedSegmentId: null,
  pendingSaves: 0,
  dirty: false,
  reelReview: EMPTY_REEL_REVIEW,
  stagedChanges: [],
  ignoredSuggestions: [],
};

// ==================== Pure derivation (unit-tested) ====================

const numberValue = (value: unknown, fallback: number): number => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const optionalSeconds = (value: unknown): number | undefined => {
  if (value === undefined || value === null || value === '') return undefined;
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric >= 0 ? numeric : undefined;
};

const optionalPositive = (value: unknown): number | undefined => {
  const numeric = optionalSeconds(value);
  return numeric !== undefined && numeric > 0 ? numeric : undefined;
};

const optionalFraction = (value: unknown): number | undefined => {
  const numeric = optionalSeconds(value);
  return numeric === undefined ? undefined : Math.min(1, numeric);
};

/** Mirrors the renderer's `formatDuration` so slide durations round identically. */
const formatDuration = (duration: number): string =>
  Number.isInteger(duration) ? String(duration) : String(Number(duration.toFixed(3)));

/**
 * The whitelist has exactly one home: `TRANSITION_NAMES` in the vendored shared
 * types, via the renderer's own guard. A second hand-written copy here is what
 * made Slide/Wipe/Flip/Clock Wipe silent no-ops — the chip wrote them, the draft
 * kept them, and this filter dropped them again before the slide was derived.
 */
const transitionValue = (value: unknown): TransitionName | undefined => {
  const transition = stringValue(value);
  return isTransitionName(transition) ? transition : undefined;
};

/** `subtitle_pos_x` / `subtitle_pos_y` / `subtitle_scale`, as the renderer reads them. */
export const subtitlePlacement = (
  metadata: Record<string, any>
): { subtitlePosX?: number; subtitlePosY?: number; subtitleScale?: number } => {
  const subtitlePosX = optionalFraction(metadata.subtitle_pos_x ?? metadata.subtitlePosX);
  const subtitlePosY = optionalFraction(metadata.subtitle_pos_y ?? metadata.subtitlePosY);
  const subtitleScale = optionalPositive(metadata.subtitle_scale ?? metadata.subtitleScale);
  return {
    ...(subtitlePosX === undefined ? {} : { subtitlePosX }),
    ...(subtitlePosY === undefined ? {} : { subtitlePosY }),
    ...(subtitleScale === undefined ? {} : { subtitleScale }),
  };
};

/** The proxy route only accepts a UUID; anything else is a hard reject. */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The id the media *file* is stored under, read off `file_url`'s basename.
 *
 * For everything the pipeline generated this is the item's own id. For the
 * second half of a split it is not: a clone shares the source's bytes, and the
 * proxy handler resolves the id in the URL straight to bytes without ever
 * reading the media row — so a clone asked for by its own id is a 404 and a
 * black player.
 *
 * But the basename is only usable when it still *is* an id.
 * `renameMediaLibraryItem` renames the file to the user's label, and the proxy
 * route rejects a non-UUID outright, so a renamed item must keep asking under
 * its own id — where its bytes have always been. A clone of a renamed item is
 * the one case neither key can serve; it is also the case the backend's own
 * delete path documents as unreachable, and a 404 there is no worse than the
 * 404 the basename would produce.
 */
export const mediaFileKey = (item: EditorMedia): string => {
  const basename = String(item.fileUrl || '')
    .split('?')[0]
    .split('/')
    .pop();
  const withoutExtension = (basename || '').replace(/\.[a-z0-9]+$/i, '');
  return UUID_PATTERN.test(withoutExtension) ? withoutExtension : item.id;
};

/**
 * The item's own file, cache-busted by its content version — the editor's
 * counterpart to `getMediaFileUrl(MediaItem)`. Note this is deliberately NOT
 * what `mediaFileKey` reads: the key is derived from the raw, un-busted
 * `fileUrl` (and strips a query string anyway), so a cache-buster can never
 * leak into a proxy path or a source-duration key.
 */
export const mediaFileUrl = (item: EditorMedia): string =>
  constructMediaUrl(item.fileUrl, item.contentVersion);

/**
 * Playable URL for a slide. Videos go through the 480p proxy so scrubbing a
 * multi-minute source stays smooth; the render always uses the original.
 */
export const slidePath = (item: EditorMedia): string =>
  item.kind === 'video'
    ? constructMediaUrl(`/media/proxy/${mediaFileKey(item)}.mp4`, item.contentVersion)
    : mediaFileUrl(item);

/** Server media item → the slide-bearing shape the editor carries. */
export const editorMediaFrom = (item: MediaItem, kind: EditorMedia['kind']): EditorMedia => {
  const contentVersion = readContentVersion(item.metadata);
  return {
    id: item.id,
    kind,
    fileUrl: item.file_url,
    ...(contentVersion === undefined ? {} : { contentVersion }),
  };
};

/** The file key of a slide by media id, for caches keyed by file rather than item. */
export const slideFileKey = (mediaId: string): string => {
  const item = state.media.find((candidate) => candidate.id === mediaId);
  return item ? mediaFileKey(item) : mediaId;
};

/**
 * The per-slide native-audio keys, read as the renderer's `nativeAudioTiming`
 * reads them. Their presence is what switches `SlideMedia` to the separated
 * `<Video muted> + <Audio>` path, so an absent key must stay absent.
 */
export const nativeAudioTiming = (
  metadata: Record<string, any>
): Pick<
  ReelSlide,
  'nativeAudioVolumePoints' | 'nativeAudioTrimStartSeconds' | 'nativeAudioTrimEndSeconds'
> => {
  const nativeAudioVolumePoints = readNativeVolumePoints(metadata);
  const nativeAudioTrimStartSeconds = optionalSeconds(
    metadata[NATIVE_AUDIO_KEYS.trimStart] ?? metadata.nativeAudioTrimStart
  );
  const nativeAudioTrimEndSeconds = optionalSeconds(
    metadata[NATIVE_AUDIO_KEYS.trimEnd] ?? metadata.nativeAudioTrimEnd
  );
  return {
    ...(nativeAudioVolumePoints.length === 0 ? {} : { nativeAudioVolumePoints }),
    ...(nativeAudioTrimStartSeconds === undefined ? {} : { nativeAudioTrimStartSeconds }),
    ...(nativeAudioTrimEndSeconds === undefined ? {} : { nativeAudioTrimEndSeconds }),
  };
};

/**
 * Mirror of the renderer's `mediaItemToSlide` + `buildTimeline`
 * (remotion/src/metadata/normalize-reel-metadata.ts): images and videos are
 * merged into one list ordered by `metadata.order`, and the per-slide keys are
 * read with the same fallbacks. Kept in the frontend because the normalizer
 * itself reads the filesystem and so cannot be vendored.
 */
export const deriveSlides = (
  media: EditorMedia[],
  mediaDrafts: Record<string, Record<string, any>>,
  clipMetadata: Record<string, any>
): ReelSlide[] => {
  const nativeVideoAudio = booleanValue(
    clipMetadata.nativeVideoAudio ?? clipMetadata.native_video_audio ?? clipMetadata.nativeAudio
  );

  return media
    .map((item) => {
      const metadata = mediaDrafts[item.id] || {};
      const rawTrimStart = optionalSeconds(metadata.trim_start ?? metadata.trimStart);
      const rawTrimEnd = optionalSeconds(metadata.trim_end ?? metadata.trimEnd);
      const inverted = hasInvertedTrim(rawTrimStart, rawTrimEnd);
      const trimStartSeconds = inverted ? undefined : rawTrimStart;
      const trimEndSeconds = inverted ? undefined : rawTrimEnd;
      const transition = transitionValue(metadata.transition_out ?? metadata.transition);
      const transitionDurationSeconds = optionalPositive(
        metadata.transition_duration ?? metadata.transitionDuration
      );
      const nativeAudioRaw = metadata.native_audio ?? metadata.nativeAudio;
      const duration = effectiveDurationSeconds({
        mediaType: item.kind,
        duration: stringValue(metadata.duration),
        trimStartSeconds,
        trimEndSeconds,
      });

      const slide: ReelSlide = {
        id: item.id,
        mediaType: item.kind,
        path: slidePath(item),
        // `subtitle_enabled: false` hides the text without losing it — the same
        // strip the render payload applies, so the preview matches.
        text: slideSubtitleEnabled(metadata) ? stringValue(metadata.text) : '',
        textPosition: stringValue(metadata.position) || 'bottom',
        role: stringValue(metadata.type) || 'slide',
        order: numberValue(metadata.order, 999),
        duration: formatDuration(duration),
        ...(transition === undefined ? {} : { transition }),
        ...(trimStartSeconds === undefined ? {} : { trimStartSeconds }),
        ...(trimEndSeconds === undefined ? {} : { trimEndSeconds }),
        ...(transitionDurationSeconds === undefined ? {} : { transitionDurationSeconds }),
        ...subtitlePlacement(metadata),
      };

      // The renderer resolves a video slide's native audio against the
      // clip-level default; images never carry one.
      if (item.kind === 'video') {
        slide.nativeAudio =
          nativeAudioRaw === undefined || nativeAudioRaw === null
            ? nativeVideoAudio
            : booleanValue(nativeAudioRaw);
        Object.assign(slide, nativeAudioTiming(metadata));
      }

      return slide;
    })
    .sort((left, right) => left.order - right.order);
};

/** Re-derives slides + timeline after any draft change. */
const withDerived = (state: ClipEditorState): ClipEditorState => {
  const slides = deriveSlides(state.media, state.mediaDrafts, state.clipMetadata);
  return { ...state, slides, timeline: computeTimeline(slides, EDITOR_FPS) };
};

/** Server clip → editor state, replacing whatever the store held. */
export const stateFromClip = (clip: ClipPrompt): ClipEditorState => {
  const images = clip.media?.images || [];
  const videos = clip.media?.ai_videos || [];
  const media: EditorMedia[] = [
    ...images.map((item: MediaItem) => editorMediaFrom(item, 'image')),
    ...videos.map((item: MediaItem) => editorMediaFrom(item, 'video')),
  ];
  const mediaDrafts: Record<string, Record<string, any>> = {};
  [...images, ...videos].forEach((item: MediaItem) => {
    mediaDrafts[item.id] = { ...(item.metadata || {}) };
  });

  return withDerived({
    ...EMPTY_STATE,
    clipId: clip.id,
    status: 'ready',
    clipName: clip.name || '',
    clipStyle: clip.style?.style || '',
    fileUrls: clip.file_urls || [],
    renderFields: {
      render_status: clip.render_status,
      render_progress: clip.render_progress,
      render_error: clip.render_error,
    },
    clipMetadata: { ...(clip.metadata || {}) },
    mediaDrafts,
    media,
    audios: clip.media?.audios || [],
    clip,
    // Server truth, reflected on every load/refresh — NOT reset by this call
    // alone; `refreshClip` re-layers the local `requesting`/`error`/staged
    // bookkeeping on top so an in-flight request or an active staged preview
    // survives a refresh triggered elsewhere in the editor.
    reelReview: {
      ...EMPTY_REEL_REVIEW,
      envelope: (clip.metadata?.reel_review as ReelReviewEnvelope | undefined) || null,
    },
  });
};

// ==================== Module-level store ====================

let state: ClipEditorState = EMPTY_STATE;
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((listener) => listener());

const setState = (next: ClipEditorState) => {
  state = next;
  emit();
  // An edit that shortens the timeline (a trim, a shorter duration, a deleted
  // slide) can strand the playhead past the end while playback is paused, which
  // would make the Player seek out of range. Every state write re-clamps it.
  clampPlayheadToTimeline();
};

export const getClipEditorState = (): ClipEditorState => state;

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const useClipEditorState = (): ClipEditorState =>
  useSyncExternalStore(subscribe, getClipEditorState);

// ==================== Playhead slice ====================

// The playhead moves ~30x/second while the preview plays. It lives in its own
// slice (the per-clip pattern of clips/renderState.ts) so that stream of
// updates only re-renders what actually reads the playhead — the header, the
// stage and every draft-driven panel stay on the editor-state subscription,
// which changes only when an edit does.

let playheadFrame = 0;
const playheadListeners = new Set<() => void>();

export const getPlayheadFrame = (): number => playheadFrame;

const subscribePlayhead = (listener: () => void): (() => void) => {
  playheadListeners.add(listener);
  return () => {
    playheadListeners.delete(listener);
  };
};

export const usePlayheadFrame = (): number =>
  useSyncExternalStore(subscribePlayhead, getPlayheadFrame);

export const setPlayheadFrame = (frame: number): void => {
  const clamped = Math.max(0, Math.min(Math.round(frame), state.timeline.totalDurationInFrames - 1));
  if (clamped === playheadFrame) return;
  playheadFrame = clamped;
  playheadListeners.forEach((listener) => listener());
};

/** Pulls a stranded playhead back inside a timeline that just got shorter. */
const clampPlayheadToTimeline = (): void => {
  const lastFrame = Math.max(0, state.timeline.totalDurationInFrames - 1);
  if (playheadFrame <= lastFrame) return;
  playheadFrame = lastFrame;
  playheadListeners.forEach((listener) => listener());
};

// ==================== Debounced batch flush ====================

// Which drafts changed since the last flush. Media items are written back as a
// full metadata map (PUT .../metadata/replace, the reorder contract); clip keys
// go one at a time through the read-modify-write editClipMetadata endpoint, so
// they are flushed sequentially to avoid clobbering each other.
const touchedMedia = new Set<string>();
const touchedClipKeys = new Set<string>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
const saveWaiters: Array<() => void> = [];

// A retry timer armed by a failed write deliberately does NOT hold waiters
// back: Render must not hang behind an endlessly retrying backend. The failure
// is visible in `saveError` instead.
const resolveWaiters = () => {
  if (state.pendingSaves > 0) return;
  const waiters = saveWaiters.splice(0, saveWaiters.length);
  waiters.forEach((resolve) => resolve());
};

const changePendingSaves = (delta: number) => {
  setState({ ...state, pendingSaves: Math.max(0, state.pendingSaves + delta) });
  resolveWaiters();
};

const errorMessage = (error: any): string => error?.message || 'Failed to save edits';

// A failure from a direct write (a lane removal, say) rather than the debounced
// flush. The flush replaces `saveError` wholesale on every run, so the message
// has to be remembered here or the next harmless flush would erase it.
let directSaveError = '';

/**
 * Writes every touched draft. A write that fails puts its id/key back in the
 * touched set and re-arms the debounce, so a transient backend failure retries
 * instead of silently dropping an edit the UI still shows as saved.
 */
const flush = async (): Promise<void> => {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  const mediaIds = Array.from(touchedMedia);
  const clipKeys = Array.from(touchedClipKeys);
  touchedMedia.clear();
  touchedClipKeys.clear();
  if (mediaIds.length === 0 && clipKeys.length === 0) {
    resolveWaiters();
    return;
  }

  const clipId = state.clipId;
  const failed: string[] = [];
  let saveError = '';
  changePendingSaves(1);

  // Snapshot every value synchronously, before the first await below. A
  // ClipEditorStage unmount runs `waitForPendingSaves()` then
  // `resetClipEditorStore()` without awaiting the former, so a flush already
  // in flight can still be mid-loop when the store resets to EMPTY_STATE —
  // reading `state.*` after that point would silently send `undefined`,
  // which a full-replace write (media) turns into a dropped key and the
  // clip-metadata merge (clip keys) turns into a deleted key server-side.
  // Snapshotting here keeps every write reading the value the user actually
  // set, regardless of what the store does later in the same tick.
  const mediaValues = mediaIds.map((id) => [id, state.mediaDrafts[id] || {}] as const);
  const clipValues = clipKeys.map((key) => [key, state.clipMetadata[key]] as const);

  await Promise.all(
    mediaValues.map(async ([mediaId, metadata]) => {
      try {
        await API.replaceMediaMetadata(mediaId, metadata);
      } catch (error: any) {
        touchedMedia.add(mediaId);
        failed.push(mediaId);
        saveError = errorMessage(error);
      }
    })
  );
  for (const [key, value] of clipValues) {
    try {
      await API.editClipMetadata(clipId, key, value);
    } catch (error: any) {
      touchedClipKeys.add(key);
      failed.push(key);
      saveError = errorMessage(error);
    }
  }

  const stillPending = touchedMedia.size > 0 || touchedClipKeys.size > 0;
  // Every write in this flush landed, so the banner has nothing left to say —
  // including about an earlier DIRECT write. Remembering that message past the
  // point where the store is demonstrably saving again is how a lane removal
  // that failed once left "Failed to remove music" on screen for the rest of
  // the session, over edits that all saved fine.
  if (!saveError) directSaveError = '';
  setState({ ...state, saveError: saveError || directSaveError, dirty: stillPending });
  if (failed.length > 0) scheduleFlush();
  changePendingSaves(-1);
};

const scheduleFlush = () => {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flush();
  }, FLUSH_DEBOUNCE_MS);
};

/**
 * Counts a save this store did not issue — the scene side panel writes through
 * the storyboard's own per-field API calls. Without this they would slip past
 * the Render gate, which only waits on `pendingSaves`. Always pair +1 with -1.
 */
export const trackExternalSave = (delta: number): void => {
  changePendingSaves(delta);
};

/**
 * Forgets queued writes for these media items. Called before reloading after an
 * external write: their draft maps are stale, so flushing them would replace the
 * value the external write just stored with the pre-edit one.
 */
export const discardPendingMediaSaves = (mediaIds: string[]): void => {
  mediaIds.forEach((mediaId) => touchedMedia.delete(mediaId));
};

/** Resolves once every debounced/in-flight save has landed. Gates Render. */
export const waitForPendingSaves = async (): Promise<void> => {
  // Kick a debounced edit off now (flush raises pendingSaves synchronously)
  // rather than awaiting it here, so the waiter below is always registered.
  if (flushTimer) void flush();
  if (state.pendingSaves === 0) return;
  await new Promise<void>((resolve) => {
    saveWaiters.push(resolve);
  });
};

// ==================== Actions ====================

export const loadClip = async (clipId: string): Promise<void> => {
  directSaveError = '';
  setState({ ...EMPTY_STATE, clipId, status: 'loading' });
  try {
    const clip: ClipPrompt = await API.getClipPrompt(clipId);
    if (!clip || clip.id !== clipId) throw new Error('Clip not found');
    setState(stateFromClip(clip));
    watchLoadedMedia();
  } catch (error: any) {
    // A watcher from a previously loaded clip is inert once this state lands
    // (its clipId guard short-circuits), but it would keep a 30s interval alive
    // for nothing until the next successful load.
    stopWatchingLoadedMedia();
    setState({
      ...EMPTY_STATE,
      clipId,
      status: 'error',
      error: error?.message || 'Failed to load clip',
    });
  }
};

/**
 * Merges a patch into one media item's draft metadata and schedules a flush.
 * A key set to `undefined` is removed from the map — the write is a full
 * replace, so that is how a transition (or a trim) is cleared.
 */
export const updateSlideMetadata = (mediaId: string, patch: Record<string, any>): void => {
  if (!state.mediaDrafts[mediaId]) return;
  const merged = { ...state.mediaDrafts[mediaId], ...patch };
  Object.keys(patch).forEach((key) => {
    if (patch[key] === undefined) delete merged[key];
  });
  setState(
    withDerived({
      ...state,
      mediaDrafts: { ...state.mediaDrafts, [mediaId]: merged },
      dirty: true,
    })
  );
  touchedMedia.add(mediaId);
  scheduleFlush();
};

/** Sets one clip metadata key in the draft and schedules a flush. */
export const updateClipMetadata = (key: string, value: any): void => {
  setState(withDerived({ ...state, clipMetadata: { ...state.clipMetadata, [key]: value }, dirty: true }));
  touchedClipKeys.add(key);
  scheduleFlush();
};

/**
 * Audio items the clip's own bucket does not carry (a library pick, a freshly
 * generated Suno track) survive a reload: the render path resolves a bound
 * `music_media_id` from the store regardless of the bucket, so the editor has
 * to be able to resolve it too.
 */
export const mergeAudioItems = (served: MediaItem[], cached: MediaItem[]): MediaItem[] => [
  ...served,
  ...cached.filter((item) => !served.some((candidate) => candidate.id === item.id)),
];

/** Caches an audio item so the lanes and the preview can resolve it right away. */
export const registerAudioItem = (item: MediaItem): void => {
  if (!item?.id) return;
  setState({
    ...state,
    audios: mergeAudioItems(
      state.audios.filter((candidate) => candidate.id !== item.id),
      [item]
    ),
  });
};

/**
 * Drops a cached audio item. `refreshClip` merges the cached list over the
 * served one, so without this a track the backend just detached would be
 * re-added by the very refetch that was meant to prove it is gone.
 */
export const forgetAudioItem = (mediaId: string): void => {
  if (!mediaId || !state.audios.some((item) => item.id === mediaId)) return;
  setState({ ...state, audios: state.audios.filter((item) => item.id !== mediaId) });
};

/**
 * Surfaces a failed direct write (not the debounced flush) in the save banner,
 * and keeps it there: pass `''` once the operation that failed has succeeded.
 */
export const setSaveError = (message: string): void => {
  directSaveError = message;
  setState({ ...state, saveError: message });
};

export const selectSlide = (slideId: string | null): void => {
  if (slideId === state.selectedSlideId && state.selectedSegmentId === null) return;
  setState({ ...state, selectedSlideId: slideId, selectedSegmentId: null });
};

/** Selecting a caption segment drops the slide selection — the panel shows one. */
export const selectSegment = (segmentId: string | null): void => {
  if (segmentId === state.selectedSegmentId && state.selectedSlideId === null) return;
  setState({ ...state, selectedSegmentId: segmentId, selectedSlideId: null });
};

// ==================== Narration tracks ====================

/**
 * Writes the caption segments. An empty list writes `undefined`, which deletes
 * the key: precedence keys off the segments being *present*, so leaving an empty
 * array behind would keep the legacy subtitle sources suppressed forever.
 */
export const setCaptionSegments = (segments: CaptionSegment[]): void => {
  updateClipMetadata('caption_segments', serializeCaptionSegments(segments));
};

/** Offset/trim for the music or narrator track; `undefined` clears a key. */
export const setTrackTiming = (
  lane: TrackLane,
  patch: { offsetSeconds?: number; trimStartSeconds?: number; trimEndSeconds?: number }
): void => {
  const keys = TRACK_KEYS[lane];
  const write = (key: string, seconds: number | undefined) => {
    // An offset of 0 is the default; storing "0" would only add a key the
    // renderer already assumes.
    const value = seconds === undefined || seconds === 0 ? undefined : String(seconds);
    if (String(state.clipMetadata[key] ?? '') === String(value ?? '')) return;
    updateClipMetadata(key, value);
  };
  if ('offsetSeconds' in patch) write(keys.offset, patch.offsetSeconds);
  if ('trimStartSeconds' in patch) write(keys.trimStart, patch.trimStartSeconds);
  if ('trimEndSeconds' in patch) write(keys.trimEnd, patch.trimEndSeconds);
};

export const setTrackVolumePoints = (lane: TrackLane, points: VolumePoint[]): void => {
  updateClipMetadata(TRACK_KEYS[lane].volumePoints, serializeVolumePoints(points));
};

/** The per-slide native-audio envelope, on the media item rather than the clip. */
export const setNativeVolumePoints = (mediaId: string, points: VolumePoint[]): void => {
  updateSlideMetadata(mediaId, {
    [NATIVE_AUDIO_KEYS.volumePoints]: serializeVolumePoints(points),
  });
};

// ==================== Live media freshness ====================

/**
 * A regenerate finishes long after the modal that started it closed.
 *
 * The generation runs in a backend goroutine; the modal's `onRefresh` fires
 * immediately, i.e. before there is anything new to read. The clip-prompts LIST
 * self-heals because a media event re-keys the whole scene panel, but the
 * mounted editor had no such path: it only ever refreshed when something in it
 * was clicked, so a finished regenerate sat unseen — the timeline kept playing
 * the take that had just been superseded, and the takes strip kept listing the
 * ledger as it stood before it.
 *
 * So the store listens. App.tsx already republishes every media socket event on
 * this window emitter (hooks/mediaLibraryEvents), which is why this needs no
 * second socket. The shape — a filtered listener plus a 30s interval so a
 * dropped frame cannot strand the view — is `pendingMusic.ts`'s, deliberately:
 * two lanes waiting on the same class of event should not wait differently.
 */
let mediaWatchDetach: (() => void) | null = null;

/** Media ids this editor is currently showing — the ones worth reacting to. */
const loadedMediaIds = (): Set<string> => {
  const ids = new Set<string>(state.media.map((item) => item.id));
  state.audios.forEach((item) => ids.add(item.id));
  return ids;
};

const stopWatchingLoadedMedia = (): void => {
  mediaWatchDetach?.();
  mediaWatchDetach = null;
};

const watchLoadedMedia = (): void => {
  if (typeof window === 'undefined') return;
  stopWatchingLoadedMedia();

  const clipId = state.clipId;
  const onInvalidate = (event: Event) => {
    const detail = (event as CustomEvent<MediaLibraryInvalidateDetail>).detail;
    // No media id means "something changed somewhere" — too broad to act on
    // without re-reading the clip on every unrelated generation in the app.
    if (!detail?.mediaId) return;
    if (state.clipId !== clipId || !loadedMediaIds().has(detail.mediaId)) return;
    void refreshClip();
  };
  window.addEventListener(MEDIA_LIBRARY_INVALIDATE_EVENT, onInvalidate);

  // Safety net: a dropped socket frame must not leave the editor showing a take
  // that no longer exists until the next click.
  const poll = window.setInterval(() => {
    if (state.clipId !== clipId) return;
    void refreshClip();
  }, 30000);

  mediaWatchDetach = () => {
    window.removeEventListener(MEDIA_LIBRARY_INVALIDATE_EVENT, onInvalidate);
    window.clearInterval(poll);
  };
};

/**
 * Re-reads the clip from the backend into the drafts, keeping the selection.
 * The scene side panel writes through the storyboard's own API calls, so
 * without this the editor's drafts would go stale and the next flush would
 * write the pre-edit values back.
 */
export const refreshClip = async (discardMediaIds: string[] = []): Promise<void> => {
  const clipId = state.clipId;
  if (!clipId) return;
  // Drop queued writes for the items the caller just wrote directly, BEFORE
  // flushing: their draft maps predate that write, so flushing them would
  // revert it and the reload below would then read back the reverted value.
  discardPendingMediaSaves(discardMediaIds);
  // Land our own remaining debounced edits, or the reload would drop them.
  await waitForPendingSaves();
  try {
    const clip: ClipPrompt = await API.getClipPrompt(clipId);
    if (!clip || clip.id !== clipId || state.clipId !== clipId) return;
    const selectedSlideId = state.selectedSlideId;
    const selectedSegmentId = state.selectedSegmentId;
    const cachedAudios = state.audios;
    const priorReelReview = state.reelReview;
    const priorStagedChanges = state.stagedChanges;
    const priorIgnoredSuggestions = state.ignoredSuggestions;
    const next = stateFromClip(clip);
    // A re-read that succeeded is the same proof a clean flush is: whatever a
    // direct write reported earlier is no longer what the clip looks like.
    directSaveError = '';
    setState({
      ...next,
      audios: mergeAudioItems(next.audios, cachedAudios),
      selectedSlideId: next.mediaDrafts[selectedSlideId || ''] ? selectedSlideId : null,
      // A segment lives in clip metadata, which the reload just replaced; the
      // lane drops the selection itself if the id is gone.
      selectedSegmentId,
      // `stateFromClip` only refreshed the server-derived envelope; an
      // in-flight request or an active staged preview belongs to THIS session,
      // not the server snapshot, so a refresh triggered elsewhere in the
      // editor must not silently drop either one.
      reelReview: {
        ...next.reelReview,
        requesting: priorReelReview.requesting,
        error: priorReelReview.error,
        // A refresh triggered elsewhere (e.g. SceneSidePanel) must not silently
        // clear an active stale banner — the mismatch is still true until the
        // user re-runs the review, regardless of what else just refetched.
        stale: priorReelReview.stale,
      },
      stagedChanges: priorStagedChanges,
      ignoredSuggestions: priorIgnoredSuggestions,
    });
  } catch (error: any) {
    setState({ ...state, saveError: errorMessage(error) });
  }
};

// ==================== Timeline editing ====================

/**
 * Restamped `order` values for a drag from `fromIndex` to `toIndex`, 0-based —
 * the same base `MediaEditorSection` writes, so the storyboard and the editor
 * speak one convention and a clip edited in both keeps its sequence.
 */
export const reorderedSlideOrders = (
  slideIds: string[],
  fromIndex: number,
  toIndex: number
): Record<string, number> => {
  const moved = [...slideIds];
  if (fromIndex < 0 || fromIndex >= moved.length || toIndex < 0 || toIndex >= moved.length) {
    return {};
  }
  const [dragged] = moved.splice(fromIndex, 1);
  moved.splice(toIndex, 0, dragged);
  const orders: Record<string, number> = {};
  moved.forEach((id, index) => {
    orders[id] = index;
  });
  return orders;
};

/**
 * Moves a block and restamps `order` across the merged image+video list. The
 * backend only restamps images on its side, so — exactly as the storyboard does
 * today — the frontend writes an order for every item in both lists.
 */
export const reorderSlides = (fromIndex: number, toIndex: number): void => {
  if (fromIndex === toIndex) return;
  const orders = reorderedSlideOrders(
    state.slides.map((slide) => slide.id),
    fromIndex,
    toIndex
  );
  Object.entries(orders).forEach(([mediaId, order]) => {
    if (numberValue(state.mediaDrafts[mediaId]?.order, NaN) === order) return;
    updateSlideMetadata(mediaId, { order });
  });
};

/** Restamps `order` 0-based across the current slide sequence. */
export const restampSlideOrders = (): void => {
  const ordered = state.slides.map((slide) => slide.id);
  ordered.forEach((mediaId, index) => {
    if (numberValue(state.mediaDrafts[mediaId]?.order, NaN) === index) return;
    updateSlideMetadata(mediaId, { order: index });
  });
};

/**
 * Adds a media item the backend just created — today only the second half of a
 * split — to the drafts, immediately after the slide it came from, and restamps
 * every order so the pair is adjacent in the merged image+video sequence.
 *
 * Idempotent by id: the clone arrives twice, once as the POST's response and
 * once as the `AddMediaItem` broadcast, and the second one must be a no-op.
 */
export const insertClonedSlide = (item: MediaItem, afterMediaId: string): void => {
  if (!item?.id || state.mediaDrafts[item.id]) return;
  const kind: EditorMedia['kind'] = item.type === 'image' ? 'image' : 'video';
  const media = [...state.media];
  const sourceIndex = media.findIndex((candidate) => candidate.id === afterMediaId);
  // Placed straight after its source in the raw list too: the derived list
  // sorts by `order`, and the two carry the same one until the restamp below.
  media.splice(sourceIndex < 0 ? media.length : sourceIndex + 1, 0, editorMediaFrom(item, kind));
  // The served clip gets it too. The backend really did attach it, and the
  // inspector is scene-shaped: it resolves the selected block through
  // `clip.media`, so without this the new piece would be a block you cannot
  // select, delete or regenerate until the next reload.
  const bucket = kind === 'image' ? 'images' : 'ai_videos';
  const clip = state.clip
    ? {
        ...state.clip,
        media: { ...state.clip.media, [bucket]: [...(state.clip.media?.[bucket] || []), item] },
      }
    : state.clip;
  setState(
    withDerived({
      ...state,
      media,
      clip,
      mediaDrafts: { ...state.mediaDrafts, [item.id]: { ...(item.metadata || {}) } },
      dirty: true,
    })
  );
  restampSlideOrders();
};

/**
 * Keeps a trim range inside `[0, natural]` and at least MIN_SLIDE_DURATION long,
 * so a drag can never produce the inverted pair the renderer has to guard on.
 * Without a probed natural duration the requested end is the only upper bound.
 */
export const clampTrimRange = (
  start: number,
  end: number,
  naturalSeconds?: number
): { start: number; end: number } => {
  const upper =
    naturalSeconds !== undefined && naturalSeconds > 0
      ? naturalSeconds
      : Math.max(end, MIN_SLIDE_DURATION_SECONDS);
  const safeStart = Math.min(Math.max(0, start), Math.max(0, upper - MIN_SLIDE_DURATION_SECONDS));
  const safeEnd = Math.min(Math.max(end, safeStart + MIN_SLIDE_DURATION_SECONDS), upper);
  return { start: round3(safeStart), end: round3(Math.max(safeEnd, safeStart + MIN_SLIDE_DURATION_SECONDS)) };
};

const round3 = (value: number): number => Number(value.toFixed(3));

/**
 * Writes a video slide's trim. `duration` is written alongside because the
 * shared timeline prefers a declared duration over the trim range (D3) — left
 * stale, a generation-time duration would keep the block at its old length.
 */
export const setSlideTrim = (mediaId: string, startSeconds: number, endSeconds: number): void => {
  // Keyed by file: both halves of a split trim against the same source length.
  const { start, end } = clampTrimRange(startSeconds, endSeconds, getSourceDuration(slideFileKey(mediaId)));
  updateSlideMetadata(mediaId, {
    trim_start: String(start),
    trim_end: String(end),
    duration: String(round3(end - start)),
  });
};

/** Sets an image slide's on-screen duration (the drag handle on its right edge). */
export const setSlideDuration = (mediaId: string, seconds: number): void => {
  updateSlideMetadata(mediaId, {
    duration: String(round3(Math.max(MIN_SLIDE_DURATION_SECONDS, seconds))),
  });
};

/**
 * Sets (or clears) the transition a slide plays into the next one. Both keys
 * live on the LEFT slide of the gap, as the renderer reads them.
 */
export const setSlideTransition = (
  mediaId: string,
  transition: TransitionName | null,
  durationSeconds?: number
): void => {
  if (transition === null) {
    updateSlideMetadata(mediaId, { transition_out: undefined, transition_duration: undefined });
    return;
  }
  updateSlideMetadata(mediaId, {
    transition_out: transition,
    ...(durationSeconds === undefined ? {} : { transition_duration: String(round3(durationSeconds)) }),
  });
};

// ==================== Whole-reel AI review (staged preview) ====================

/** `MediaItem.prompt` lives on the served clip, not on the editor's own drafts. */
const promptByMediaId = (): Record<string, string> => {
  const map: Record<string, string> = {};
  const clip = state.clip;
  if (!clip) return map;
  [...(clip.media?.images || []), ...(clip.media?.ai_videos || [])].forEach((item) => {
    map[item.id] = item.prompt || '';
  });
  return map;
};

/** Executes one planned change through the SAME actions a manual edit uses. */
const applyStagedChangeToStore = (change: StagedChange): void => {
  if (change.kind === 'gap') {
    if (change.after.transition) setSlideTransition(change.mediaId, change.after.transition, change.after.duration);
    return;
  }
  if (change.after.trimStart !== undefined && change.after.trimEnd !== undefined) {
    setSlideTrim(change.mediaId, change.after.trimStart, change.after.trimEnd);
    return;
  }
  if (change.after.duration !== undefined) {
    setSlideDuration(change.mediaId, change.after.duration);
  }
};

/** The boundary map + hash the "AI REVIEW" button POSTs, off the CURRENT timeline. */
export const buildReelReviewRequestBody = (): {
  boundaries: ReturnType<typeof buildReelReviewBoundaries>;
  gaps: ReturnType<typeof buildReelReviewGaps>;
  boundaries_hash: string;
} => {
  const boundaries = buildReelReviewBoundaries(state.timeline, EDITOR_FPS, promptByMediaId());
  const gaps = buildReelReviewGaps(state.timeline, EDITOR_FPS);
  return { boundaries, gaps, boundaries_hash: computeBoundariesHash(boundaries, gaps) };
};

/** The one actionable message for a misconfigured (non-video-capable) judge model. */
export const JUDGE_MODEL_NOT_VIDEO_CAPABLE_MESSAGE =
  "The reviewer's judge model can't watch video — pick a video-capable model in Settings → Reviewer (Clip QA / Confirm).";

/**
 * How long the button stays in its "Reviewing…" state before giving up. The
 * report arrives over the WS `ReelReviewCompleted` event, so a dropped socket,
 * a backend restart mid-run or a goroutine that died leaves NOTHING to clear
 * `requesting` — and the button would sit disabled until the editor is
 * reloaded. Comfortably past the backend's own 10-minute judge timeout is the
 * wrong tradeoff here: three minutes covers a normal reel, and re-running is
 * cheap next to a stuck editor.
 */
export const REEL_REVIEW_TIMEOUT_MS = 3 * 60 * 1000;

export const REEL_REVIEW_TIMEOUT_MESSAGE =
  'The AI review is taking longer than expected — no report arrived. Try running it again.';

let reelReviewTimer: ReturnType<typeof setTimeout> | null = null;

const clearReelReviewTimeout = (): void => {
  if (reelReviewTimer) clearTimeout(reelReviewTimer);
  reelReviewTimer = null;
};

/**
 * Arms the give-up timer for ONE request. It fires only while that same clip is
 * still open and still waiting — a report that landed first, a different clip,
 * or a second request all leave it with nothing to do.
 */
const armReelReviewTimeout = (clipId: string): void => {
  clearReelReviewTimeout();
  reelReviewTimer = setTimeout(() => {
    reelReviewTimer = null;
    if (state.clipId !== clipId || !state.reelReview.requesting) return;
    setState({
      ...state,
      reelReview: { ...state.reelReview, requesting: false, error: REEL_REVIEW_TIMEOUT_MESSAGE },
    });
  }, REEL_REVIEW_TIMEOUT_MS);
};

/**
 * Kicks off a whole-reel review. Stays `requesting` until `ReelReviewCompleted`
 * (WS) triggers a refetch and `stageReelReviewSuggestions` below, or the
 * REEL_REVIEW_TIMEOUT_MS give-up timer fires — there is no synchronous report
 * here, only a 202. Refused while a previous batch is still
 * staged: re-running would flush those staged (but unreviewed) edits to the
 * server the moment the WS handler's own refetch calls `waitForPendingSaves`.
 */
export const requestReelReview = async (): Promise<void> => {
  if (state.reelReview.requesting || state.stagedChanges.length > 0) return;
  const clipId = state.clipId;
  setState({ ...state, reelReview: { ...state.reelReview, requesting: true, error: '' } });
  armReelReviewTimeout(clipId);
  try {
    await API.reviewReel(clipId, buildReelReviewRequestBody());
  } catch (error: any) {
    clearReelReviewTimeout();
    const status = error?.response?.status;
    const message =
      status === 409
        ? 'Render the clip before running AI review.'
        : status === 422
          ? JUDGE_MODEL_NOT_VIDEO_CAPABLE_MESSAGE
          : errorMessage(error);
    setState({ ...state, reelReview: { ...state.reelReview, requesting: false, error: message } });
  }
};

/**
 * Stages whatever `clipMetadata.reel_review` currently holds: rebuilds the
 * boundary map fresh, compares its hash against the report's own, and — only
 * on a match — plans and immediately applies every suggestion through the
 * normal trim/duration/transition actions (cut-seconds resolved against each
 * slide's CURRENT absolute trim/duration). Called once, right after the WS
 * `ReelReviewCompleted` handler's own refetch — never from a generic refresh,
 * or an unrelated clip reload would silently re-stage an old report.
 *
 * `expectedClipId`, when given, must match the CURRENT `state.clipId` or this
 * is a no-op. The WS handler awaits a refetch before calling this, and the
 * user can switch clips during that await — without this guard, a report for
 * clip A would land on whatever clip B the editor opened in the meantime.
 */
export const stageReelReviewSuggestions = (expectedClipId?: string): void => {
  if (expectedClipId !== undefined && state.clipId !== expectedClipId) return;
  const envelope = state.clipMetadata.reel_review as ReelReviewEnvelope | undefined;
  if (!envelope || !envelope.report) return;
  const { boundaries, gaps, boundaries_hash } = buildReelReviewRequestBody();
  const stale = boundaries_hash !== envelope.boundaries_hash;
  const plan = stale
    ? { staged: [], ignored: [] }
    : planStagedChanges(envelope.report, boundaries, gaps, state.slides, state.mediaDrafts);
  plan.staged.forEach(applyStagedChangeToStore);
  clearReelReviewTimeout(); // the report arrived — nothing left to give up on
  setState({
    ...state,
    reelReview: { requesting: false, error: '', envelope, stale },
    stagedChanges: plan.staged,
    ignoredSuggestions: plan.ignored,
  });
};

/** Reverts just one staged change, from its exact pre-stage snapshot. */
export const revertStagedChange = (id: string): void => {
  const change = state.stagedChanges.find((candidate) => candidate.id === id);
  if (!change) return;
  updateSlideMetadata(change.mediaId, change.before);
  setState({ ...state, stagedChanges: state.stagedChanges.filter((candidate) => candidate.id !== id) });
};

/** Restores every staged change's snapshot verbatim; the report stays readable. */
export const declineReelReview = (): void => {
  state.stagedChanges.forEach((change) => updateSlideMetadata(change.mediaId, change.before));
  setState({ ...state, stagedChanges: [], ignoredSuggestions: [] });
};

/**
 * Keeps the staged edits exactly as they are, stamps `accepted_at` onto the
 * stored envelope, and flushes now (the normal save path, run once) rather
 * than waiting out the debounce.
 */
export const acceptReelReview = async (): Promise<void> => {
  const envelope = state.reelReview.envelope;
  if (envelope) {
    const accepted: ReelReviewEnvelope = { ...envelope, accepted_at: new Date().toISOString() };
    updateClipMetadata('reel_review', accepted);
    setState({
      ...state,
      reelReview: { ...state.reelReview, envelope: accepted },
      stagedChanges: [],
      ignoredSuggestions: [],
    });
  } else {
    setState({ ...state, stagedChanges: [], ignoredSuggestions: [] });
  }
  await waitForPendingSaves();
};

/** Drops all editor state — used on unmount and by tests. */
export const resetClipEditorStore = (): void => {
  directSaveError = '';
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = null;
  clearReelReviewTimeout();
  stopWatchingLoadedMedia();
  touchedMedia.clear();
  touchedClipKeys.clear();
  // Anyone awaiting a save (Render, in particular) must be released rather than
  // left hanging on a promise this store will never settle.
  saveWaiters.splice(0, saveWaiters.length).forEach((resolve) => resolve());
  state = EMPTY_STATE;
  playheadFrame = 0;
  emit();
  playheadListeners.forEach((listener) => listener());
};
